import { SafeWalletAbi } from '@repo/contracts'
import { type Address, isAddressEqual, type PublicClient } from 'viem'

const LOW_THRESHOLD_RATIO = 0.4

export const SafeWalletHealthWarning = {
  SinglePointOfFailure: 'SINGLE_POINT_OF_FAILURE',
  LowThresholdRatio: 'LOW_THRESHOLD_RATIO',
  RoleOverlap: 'ROLE_OVERLAP'
} as const

export type SafeWalletHealthWarning =
  (typeof SafeWalletHealthWarning)[keyof typeof SafeWalletHealthWarning]

type SafeWalletHealthInput = {
  threshold: number
  owners: readonly Address[]
  overlapAddress?: Address
}

/** @internal */
export function computeSafeWalletHealthWarnings({
  threshold,
  owners,
  overlapAddress
}: SafeWalletHealthInput) {
  const warnings: SafeWalletHealthWarning[] = []

  if (threshold === 1) {
    warnings.push(SafeWalletHealthWarning.SinglePointOfFailure)
  }

  if (owners.length > 0 && threshold / owners.length <= LOW_THRESHOLD_RATIO) {
    warnings.push(SafeWalletHealthWarning.LowThresholdRatio)
  }

  if (overlapAddress && owners.some(owner => isAddressEqual(owner, overlapAddress))) {
    warnings.push(SafeWalletHealthWarning.RoleOverlap)
  }

  return warnings
}

type SafeWalletInfo =
  | { isSafeWallet: true; threshold: number; owners: readonly Address[] }
  | { isSafeWallet: false }

export type SafeWalletHealth = SafeWalletInfo & {
  healthWarnings: SafeWalletHealthWarning[]
}

export async function fetchSafeWalletHealth(
  client: PublicClient,
  address: Address,
  overlapAddress?: Address
): Promise<SafeWalletHealth> {
  const results = await client.multicall({
    contracts: [
      { address, abi: SafeWalletAbi, functionName: 'getThreshold' },
      { address, abi: SafeWalletAbi, functionName: 'getOwners' }
    ],
    allowFailure: true
  })

  const [thresholdResult, ownersResult] = results

  if (thresholdResult.status !== 'success' || ownersResult.status !== 'success') {
    return { isSafeWallet: false, healthWarnings: [] }
  }

  const info = {
    isSafeWallet: true as const,
    threshold: Number(thresholdResult.result),
    owners: ownersResult.result
  }

  return {
    ...info,
    healthWarnings: computeSafeWalletHealthWarnings({ ...info, overlapAddress })
  }
}
